import { FileWithTimestamp } from '@features/components/ClinicalData/types';
import { usePreviewFile } from '@features/components/ClinicalData/hooks';
import { convertMediaFiles } from '@features/components/ClinicalData/utils';
import { useMemo } from 'react';
import { ClinicalGalleryItem } from '@features/components/ClinicalData/components/ClinicalGalleryItem';
import { ClinicalNavigation } from '@features/components/ClinicalData/components/ClinicalNavigation';
import { MediaPreview } from '@features/components/ClinicalData/components/MediaPreview/MediaPreview';
import { MdPlayCircleFilled } from 'react-icons/md';
import { ClinicalMediaFile } from '@/api/apis/case';
import { useRowScroll } from '@/hooks/useRowScroll';
import { buildResizedImageUrl } from '@/utils/helpers/url.helpers';
import { IMAGE_SIZES } from '@/constants/image.constants';

type GalleryProps = {
  heading?: string;
  showNavigation?: boolean;
  mediaFiles?: FileWithTimestamp[];
  selectedFileIndex?: number;
  setSelectedFileIndex?: (i: number) => void;
  isFromClinicalLog?: boolean;
  onShareClick?: () => void;
};

const getDurationLabel = (file: ClinicalMediaFile) => {
  return file.mediaType === 'Video' ? (
    <div className="flex items-center gap-0.5">
      <MdPlayCircleFilled />
      {file.duration}s
    </div>
  ) : (
    ''
  );
};

export const Gallery = ({
  heading,
  showNavigation = true,
  mediaFiles,
  selectedFileIndex = 0,
  setSelectedFileIndex = () => {},
  isFromClinicalLog,
  onShareClick,
}: GalleryProps) => {
  const { listContainerRef, listItemRefs, onNavigationPrev, onNavigationNext } = useRowScroll();
  const { isPreviewOpen, previewFileId, handlePreviewOpen, handlePreviewClose } = usePreviewFile();
  const mediaList = useMemo(
    () => mediaFiles?.flatMap((item) => (item ? convertMediaFiles([item], item.timestamp, item.timeZone) : [])),
    [mediaFiles],
  );

  if (!mediaFiles?.length) return null;

  const handlePrev = () => {
    if (selectedFileIndex > 0) {
      setSelectedFileIndex(selectedFileIndex - 1);
      onNavigationPrev();
    }
  };

  const handleNext = () => {
    if (selectedFileIndex < mediaFiles.length - 1) {
      setSelectedFileIndex(selectedFileIndex + 1);
      onNavigationNext();
    }
  };

  const setSelectedFile = (i: number, fileId: string) => {
    setSelectedFileIndex?.(i);
    handlePreviewOpen(fileId);
  };

  const getFileForThumb = (f: FileWithTimestamp) =>
    f.mediaType === 'Photo' ? { ...f, path: buildResizedImageUrl(f.path, IMAGE_SIZES.THUMBNAIL) } : f.preview;

  return (
    <>
      <div className="scroll flex flex-col gap-2 w-full">
        <div className="grid grid-flow-col auto-cols-max gap-2 pb-1 relative overflow-auto" ref={listContainerRef}>
          {mediaFiles.map((f, i) => (
            <ClinicalGalleryItem
              key={f.fileId}
              ref={(el) => {
                listItemRefs.current[i] = el;
              }}
              file={getFileForThumb(f)}
              containerClassName={i === selectedFileIndex ? 'border-base-20' : ''}
              onClick={() => setSelectedFile(i, f.fileId)}
              bottomLabel={getDurationLabel(f)}
            />
          ))}
        </div>
        {showNavigation && (
          <ClinicalNavigation
            label={`${mediaFiles.length} file${mediaFiles.length > 1 ? 's' : ''} attached`}
            prev={handlePrev}
            next={handleNext}
            prevDisabled={selectedFileIndex === 0}
            nextDisabled={selectedFileIndex === mediaFiles.length - 1}
          />
        )}
      </div>
      {isPreviewOpen && (
        <MediaPreview
          heading={heading}
          media={mediaList || []}
          onClose={handlePreviewClose}
          open={isPreviewOpen}
          selectedMediaId={previewFileId}
          isFromClinicalLog={isFromClinicalLog}
          onShareClick={onShareClick}
        />
      )}
    </>
  );
};
